import { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Pencil, Check, X } from 'lucide-react';

const MATCH_BADGE = {
  MATCHED: 'bg-emerald-100 text-emerald-800',
  ITEM_NOT_ON_PO: 'bg-red-100 text-red-800',
  NO_MATCH: 'bg-slate-100 text-slate-700',
};

export function LineRow({ line, onSave, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    description: line.pi_description || '', part_number: line.pi_part_number || '',
    qty: line.pi_qty ?? '', unit_price: line.pi_unit_price ?? '', line_total: line.pi_line_total ?? '',
  });
  const [saving, setSaving] = useState(false);

  const orig = line.original || {};
  const cell = (field, current) => {
    const wasCorrected = line.corrected && orig[field] !== undefined && String(orig[field] ?? '') !== String(current ?? '');
    return wasCorrected ? (
      <span className="italic text-amber-600" title={`Originally extracted: ${orig[field] ?? 'null'}`} data-testid={`line-original-${field}-${line.line_no}`}>
        was: {orig[field] ?? 'null'}
      </span>
    ) : null;
  };

  const startEdit = () => {
    setForm({
      description: line.pi_description || '', part_number: line.pi_part_number || '',
      qty: line.pi_qty ?? '', unit_price: line.pi_unit_price ?? '', line_total: line.pi_line_total ?? '',
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(line.id, {
        description: form.description, part_number: form.part_number || null,
        qty: form.qty === '' ? null : parseFloat(form.qty),
        unit_price: form.unit_price === '' ? null : parseFloat(form.unit_price),
        line_total: form.line_total === '' ? null : parseFloat(form.line_total),
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-b border-border" data-testid={`pi-line-row-${line.line_no}`}>
      {editing ? (
        <>
          <td className="p-2"><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-7 text-xs" data-testid={`line-edit-desc-${line.line_no}`} /></td>
          <td className="p-2"><Input value={form.part_number} onChange={e => setForm(f => ({ ...f, part_number: e.target.value }))} className="h-7 text-xs" data-testid={`line-edit-part-${line.line_no}`} /></td>
          <td className="p-2"><Input type="number" value={form.qty} onChange={e => setForm(f => ({ ...f, qty: e.target.value }))} className="h-7 text-xs w-20" data-testid={`line-edit-qty-${line.line_no}`} /></td>
          <td className="p-2"><Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm(f => ({ ...f, unit_price: e.target.value }))} className="h-7 text-xs w-24" data-testid={`line-edit-price-${line.line_no}`} /></td>
          <td className="p-2"><Input type="number" step="0.01" value={form.line_total} onChange={e => setForm(f => ({ ...f, line_total: e.target.value }))} className="h-7 text-xs w-24" data-testid={`line-edit-total-${line.line_no}`} /></td>
          <td colSpan={3} className="p-2">
            <div className="flex gap-1">
              <Button size="icon" className="h-7 w-7 rounded-sm" disabled={saving} onClick={save} data-testid={`line-save-${line.line_no}`}><Check className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="outline" className="h-7 w-7 rounded-sm" onClick={() => setEditing(false)} data-testid={`line-cancel-${line.line_no}`}><X className="h-3.5 w-3.5" /></Button>
            </div>
          </td>
        </>
      ) : (
        <>
          <td className="p-2 text-sm max-w-[220px] truncate" title={line.pi_description}>
            {line.pi_description || <span className="text-muted-foreground italic">unreadable</span>}
            {cell('description', line.pi_description) && <div className="text-[10px]">{cell('description', line.pi_description)}</div>}
          </td>
          <td className="p-2 text-xs font-mono">
            {line.pi_part_number || '—'}
            {cell('part_number', line.pi_part_number) && <div className="text-[10px]">{cell('part_number', line.pi_part_number)}</div>}
          </td>
          <td className="p-2 text-sm text-right font-mono">
            {line.pi_qty ?? <span className="text-red-500">null</span>}
            {cell('qty', line.pi_qty) && <div className="text-[10px]">{cell('qty', line.pi_qty)}</div>}
          </td>
          <td className="p-2 text-sm text-right font-mono">
            {line.pi_unit_price ?? <span className="text-red-500">null</span>}
            {cell('unit_price', line.pi_unit_price) && <div className="text-[10px]">{cell('unit_price', line.pi_unit_price)}</div>}
          </td>
          <td className="p-2 text-sm text-right font-mono">
            {line.pi_line_total ?? <span className="text-red-500">null</span>}
            {cell('line_total', line.pi_line_total) && <div className="text-[10px]">{cell('line_total', line.pi_line_total)}</div>}
          </td>
          <td className="p-2 text-xs">
            {line.po_qty != null ? <span className="font-mono">{line.po_qty} @ {line.po_rate}</span> : <span className="text-muted-foreground">—</span>}
          </td>
          <td className="p-2"><Badge className={`text-[10px] rounded-sm ${MATCH_BADGE[line.match_status] || MATCH_BADGE.NO_MATCH}`}>{(line.match_status || 'NO MATCH').replace(/_/g, ' ')}</Badge></td>
          <td className="p-2 text-right">
            {!readOnly && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={startEdit} data-testid={`line-edit-btn-${line.line_no}`}><Pencil className="h-3.5 w-3.5" /></Button>}
          </td>
        </>
      )}
    </tr>
  );
}
